import { Button, FormField, TextArea, TextInput } from 'canoe';
import { Field, FieldInputProps, FieldMetaProps, Form, Formik, FormikProps } from 'formik';
import { ENDPOINTS, useActions } from 'hammock';
import React from 'react';
import * as Yup from 'yup';

import { CollectionOperation } from '@candu-ai/collections/types';

export interface UpsertCollectionProps {
  context?: {
    canCreate?: boolean;
    canEdit?: boolean;
  };
  collectionName?: string;
  description?: string;
  collectionId?: string;
  onCollectionUpsertSuccess?: (e: { id: string }) => void;
  onCollectionUpsertFail?: VoidFunction;
}

const validationSchema = Yup.object().shape({
  collectionName: Yup.string().required('A collection name is required').max(50),
  description: Yup.string().notRequired().max(500),
});

export const UpsertCollection: React.FC<UpsertCollectionProps> = ({
  context,
  collectionName = '',
  description = '',
  collectionId,
  onCollectionUpsertSuccess,
  onCollectionUpsertFail,
}: UpsertCollectionProps) => {
  const { create, action } = (useActions(ENDPOINTS.collections) as unknown) as {
    create: (payload: any) => Promise<{ id: string }>;
    action: (payload: any) => Promise<{ id: string }>;
  };
  const onSubmit = async (
    payload: { collectionName: string; description: string },
    { resetForm }: FormikProps<any>,
  ) => {
    try {
      if (collectionId) {
        // editing
        const response = await action({
          name: CollectionOperation.UpdateMetadata,
          body: {
            collectionId,
            collectionName: payload.collectionName,
            metadata: {
              description: payload.description,
            },
          },
          onSuccessNotification: {
            message: 'Changes saved successfully',
          },
          invalidates: {
            [ENDPOINTS.collections]: true,
            [ENDPOINTS.collectionContent]: { id: collectionId },
          },
        });
        resetForm({ values: payload });
        onCollectionUpsertSuccess?.(response);
      } else {
        // creating
        const response = await create({
          body: {
            collectionName: payload.collectionName,
            metadata: {
              description: payload.description,
            },
            collectionType: 'CMS',
          },
        });
        onCollectionUpsertSuccess?.(response);
      }
    } catch (e) {
      onCollectionUpsertFail?.();
    }
  };

  const initialValues = {
    collectionName,
    description,
  };

  const submitDisabled =
    (collectionId && !context?.canEdit) || (!collectionId && !context?.canCreate);
  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {(form: FormikProps<any>) => (
        <Form>
          <Field required name="collectionName" placeholder="Doe">
            {({
              field,
              meta,
            }: {
              field: FieldInputProps<'collectionName'>;
              meta: FieldMetaProps<'collectionName'>;
            }) => (
              <FormField
                feedback={meta.touched ? meta.error : ''}
                state={meta.touched && meta.error ? 'danger' : 'none'}
                label="Collection name"
                necessity="required"
                data-cy="collectionName"
              >
                <TextInput
                  autoFocus
                  maxLength={50}
                  placeholder="Write the name of your new collection"
                  {...field}
                />
              </FormField>
            )}
          </Field>
          <br />
          <Field required name="description" placeholder="Doe">
            {({
              field,
              meta,
            }: {
              field: FieldInputProps<'description'>;
              meta: FieldMetaProps<'description'>;
            }) => (
              <FormField
                feedback={meta.touched ? meta.error : ''}
                state={meta.touched && meta.error ? 'danger' : 'none'}
                label="Description"
                data-cy="description"
              >
                <TextArea maxLength={500} placeholder="Write description" rows={3} {...field} />
              </FormField>
            )}
          </Field>
          <br />

          <Button
            appearance="outline"
            data-cy={!collectionId ? 'create-collection' : 'update-collection'}
            disabled={!(form.isValid && form.dirty && !form.isSubmitting && !submitDisabled)}
            variant={collectionId ? 'secondary' : 'orange'}
            type="submit"
          >
            {collectionId ? 'Save changes' : 'Create New Collection'}
          </Button>
        </Form>
      )}
    </Formik>
  );
};
